<template>
	<div class="report-details">
		<div class="report-header">
			<CMobileSearch
				:value.sync="searchValue"
				:placeholder="$t('enterprise.default-search')"
				@change="changeSearch"
			/>

			<template v-if="isSlideTmpl">
				<m-inline-dropdown
					class="dropdown"
					:value.sync="activeType"
					:options="typeRange"
					position="right"
					:defaultText="$t('enterprise.type')"
					@change="value => handleChangeDropDown(value, 'type')"
				>
					<template v-slot:title="props">
						<span class="dropdown-menu-text">{{ props.actived.text }}</span>
						<span class="dropdown-angle-icon"></span>
					</template>
				</m-inline-dropdown>

				<m-inline-dropdown
					class="dropdown"
					:value.sync="activeTime"
					:options="timeRange"
					position="right"
					:defaultText="$t('enterprise.time')"
					@change="value => handleChangeDropDown(value, 'time')"
				>
					<template v-slot:title="props">
						<span class="dropdown-menu-text">{{ props.actived.text }}</span>
						<span class="dropdown-angle-icon"></span>
					</template>
				</m-inline-dropdown>
			</template>
		</div>

		<div class="report-list-wrapper">
			<CMobileScroll
				ref="scrollRef"
				pullDown
				@pull-down="onPullDown"
				pullUp
				:moreData="hasMoreData"
				@pull-up="onPullUp"
				v-loading="isLoading"
			>
				<div class="report-list" v-if="finalData.length">
					<div class="report-box-item" v-for="item in finalData" :key="item.id" @click="handleDetail(item)">
						<div class="file-icon">
							<SvgIcons :fileUrl="item.url" isFile class="icon" />
						</div>

						<div class="report-wrapper">
							<div class="report-wrapper-title text-overflow">
								{{ item.title }}
							</div>
							<div class="report-wrapper-footer">
								<span class="org-name">
									{{ item.orgName }}
								</span>
								<span class="time">{{ item.publishDate }}</span>
							</div>
						</div>
					</div>
				</div>
				<Empty v-if="isEmpty" />
			</CMobileScroll>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, computed, getCurrentInstance } from 'vue';
import CMobileSearch from '@/components/mobile/CMobileSearch.vue';
import CMobileScroll from '@/components/mobile/CMobileScroll.vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useModuleConfig } from '@rebuild/hooks/data/useModuleConfig';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';
import { usePaginationMobile } from '@rebuild/hooks/load-more/usePaginationMobile';
import { useCommonData } from '@/hooks';
import { getTimeStamp } from '@/utils/api';

const { proxy } = getCurrentInstance();
const { report } = useModuleData();
const { config, loadModuleConfig } = useModuleConfig();
const { viewModule } = useModuleNavigation();

const { isSlideTmpl } = useCommonData();

const searchValue = ref('');
const activeType = ref('');
const activeTime = ref('');

const typeRange = computed(() => {
	if (!isSlideTmpl.value) {
		return [
			{ text: proxy.$t('enterprise.all'), value: '' },
			{ text: proxy.$t('enterprise.company-report'), value: 0 },
			{ text: proxy.$t('enterprise.industry-report'), value: 1 }
		];
	}

	const newTabs = config.value?.tab
		? config.value?.tab
				.filter(item => item.isPublish)
				.map(item => ({
					...item,
					text: item.name,
					value: item.value || item.id
				}))
		: [];

	return [{ text: proxy.$t('enterprise.all'), value: '' }, ...newTabs];
});

const timeRange = computed(() => [
	{ value: '', text: proxy.$t('enterprise.all') },
	{ value: 'lastOneYear', text: proxy.$t('enterprise.lastOneYear') },
	{ value: 'lastThreeYear', text: proxy.$t('enterprise.lastThreeYear') },
	{ value: 'lastFiveYear', text: proxy.$t('enterprise.lastFiveYear') }
]);

const isLoading = computed(() => report.loading);
const isEmpty = computed(() => report.empty || !finalData.value.length);

const { currentPage, pageSize, finalData, hasMoreData, setData, onPullDown, onPullUp } = usePaginationMobile({
	bindChange: handlePaginationChange
});

const handleChangeDropDown = (item, type) => {
	if (type == 'time') {
		activeTime.value = item.value;
	}
	if (type == 'type') {
		activeType.value = item.value;
	}
	refreshData();
};

const changeSearch = () => {
	refreshData();
};

async function handlePaginationChange() {
	const params = {
		pagestart: currentPage.value - 1,
		pagenum: pageSize.value,
		keyword: searchValue.value
	};

	if (activeType.value !== '') {
		params.rating = [activeType.value];
	}

	if (activeTime.value) {
		const timeStamps = getTimeStamp(activeTime.value);
		params.beginTime = timeStamps[0];
		params.endTime = timeStamps[1];
	}

	await report.refresh(params);

	const data = report.data?.rows || [];
	const total = report.data?.total || 0;
	setData(data, total);
}

const refreshData = async () => {
	currentPage.value = 1;
	await handlePaginationChange();
};

const handleDetail = item => {
	viewModule('report', item);
};

onMounted(async () => {
	if (isSlideTmpl.value) {
		await loadModuleConfig('report');
	}
	await refreshData();
});
</script>

<style lang="less" scoped>
.report-details {
	height: 100%;

	.report-header {
		display: flex;
		padding-right: 16px;
		.dropdown + .dropdown {
			margin-left: 16px;
		}
	}

	.report-list-wrapper {
		height: 100%;
		.report-list {
			padding: 10px 16px;
		}
	}
}

.report-box-item {
	display: flex;
	align-items: flex-start;
	padding: 16px 0;
	border-bottom: 1px solid #f2f3f5;
	cursor: pointer;

	&:last-child {
		border-bottom: none;
	}

	.file-icon {
		margin-right: 12px;
		flex-shrink: 0;
		.icon {
			width: 36px;
			height: 36px;
		}
	}

	.report-wrapper {
		flex: 1;
		min-width: 0;

		&-title {
			font-size: 14px;
			font-weight: 500;
			color: #1d2129;
			line-height: 1.4;
			margin-bottom: 8px;
		}

		&-footer {
			display: flex;
			align-items: center;
			font-size: 12px;
			gap: 12px;

			.type {
				padding: 2px 6px;
				border-radius: 4px;
				font-weight: 500;

				&.rating-0 {
					background: #e8f5e8;
					color: #52c41a;
				}
				&.rating-1 {
					background: #fff7e6;
					color: #fa8c16;
				}
				&.rating-2 {
					background: #f6f6f6;
					color: #666;
				}
			}

			.time {
				color: #86909c;
			}
			// 颜色样式类名
			.rating-1 {
				color: #f23d3d;
				background: linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%), #f23d3d;
			}
			.rating-2 {
				color: #30bf54;
				background: linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%), #36d95f;
			}
			.rating-3 {
				color: #3964e5;
				background: linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%), #3964e5;
			}
			.rating-4 {
				color: var(--t-secondary-info-color, #86909c);
				background: var(--fill-normal-color, #f2f3f5);
			}
		}
	}
}

.dropdown-menu-text {
	font-size: 14px;
	font-weight: 400;
	line-height: 18px;
}
</style>
